import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Params } from '@angular/router';
import { QarepaApiLinks } from '@qarepa/common';
import { OAuthStorageService } from '@qaroni-app/auth/services/o-auth-storage.service';
import { CommonsHttpService } from '@qaroni-core/services/commons/commons-http/commons-http.service';
import { Observable, Subject } from 'rxjs';
import { shareReplay } from 'rxjs/operators';
import { Check, CheckCreateDto, CheckReport } from '../types/check';
import { CheckApiResponse } from '../types/check-response';
import { CheckEmployeeRequest } from '../types/check-employee-request';
import { CheckHistory } from '../types/check-history';
import { CheckHistoryApiObject } from '../types/check-history-response';
import { CheckRegisterApiResponse } from '../types/check-register-response';
import { CheckReportApiResponse } from '../types/check-report-response';
import { Journey } from '../types/journey';
import { Month } from '../types/period';
import { CheckHttpService } from './check-http.service';
import { CheckSnackbarsService } from './check-snackbars.service';

@Injectable({
  providedIn: 'root',
})
export class CheckService {
  private http = inject(CheckHttpService);
  private commonsHttp = inject(CommonsHttpService);
  private oAuthStorage = inject(OAuthStorageService);
  private snackbars = inject(CheckSnackbarsService);

  protected readonly checksSubject = new Subject<Check[]>();
  protected readonly checkSubject = new Subject<Check>();
  protected readonly journeySubject = new Subject<Journey>();
  protected readonly messageSubject = new Subject<string>();
  protected readonly checkHistorySubject = new Subject<CheckHistory[]>();
  protected readonly checkRequestSubject = new Subject<CheckEmployeeRequest>();
  protected readonly paginationLinksSubject = new Subject<QarepaApiLinks>();
  protected readonly checksReportSubject = new Subject<CheckReport>();

  // ==========================================================================================
  // Register Check
  // ==========================================================================================

  public getCheck$(): Observable<Check> {
    return this.checkSubject.asObservable().pipe(shareReplay(1));
  }

  public getCheckRequest$(): Observable<CheckEmployeeRequest> {
    return this.checkRequestSubject.asObservable().pipe(shareReplay(1));
  }

  public getJourney$(): Observable<Journey> {
    return this.journeySubject.asObservable().pipe(shareReplay(1));
  }

  public getCheckHistorySubject$(): Observable<CheckHistory[]> {
    return this.checkHistorySubject.asObservable().pipe(shareReplay(1));
  }

  public registerCheck(employeeID: number, check: CheckCreateDto): void {
    if (!this.oAuthStorage.hasOAuth) {
      return;
    }

    this.http.registerCheck$(employeeID, check).subscribe({
      next: this.nextRegisterCheck,
      error: this.errorRegisterCheck,
    });
  }

  private nextRegisterCheck = (data: CheckRegisterApiResponse): void => {
    if (this.commonsHttp.validationsHttp.verifyStatus201(data)) {
      const result = data.body.result[0];
      const journey: Journey = data.body.included as unknown as Journey;

      if (this.isCheck(result)) {
        this.checkSubject.next(result);
        this.journeySubject.next(journey);
        this.snackbars.successRegisterCheck();
      } else {
        this.checkRequestSubject.next(result);
        this.snackbars.successRequestRegisterCheck();
      }
    } else {
      this.checkSubject.next(null);
      this.journeySubject.next(null);
      this.commonsHttp.errorsHttp.apiInvalidResponse(data);
    }
  };

  private errorRegisterCheck = (error: HttpErrorResponse): void => {
    this.checkSubject.next(null);
    this.journeySubject.next(null);
    if (this.commonsHttp.errorsHttp.isControlledError(error, 'E0004')) {
      this.snackbars.failureWrongHours();
    } else if (this.commonsHttp.errorsHttp.isControlledError(error, 'E0008')) {
      this.snackbars.failureEmployeeInvalidCredentials();
    } else if (this.commonsHttp.errorsHttp.isControlledError(error, 'E0020')) {
      this.snackbars.failureEmployeeNotWithInWorkplaceAccessRadius();
    } else if (this.commonsHttp.errorsHttp.isControlledError(error, 'E0028')) {
      this.snackbars.failureCheckDateCollidesWithAnExistingOne();
    } else if (this.commonsHttp.errorsHttp.isControlledError(error, 'E0030')) {
      this.snackbars.failureCheckCalendarIsClosed();
    } else if (this.commonsHttp.errorsHttp.isControlledError(error, 'E0069')) {
      this.snackbars.failureCheckDateCollidesWithAnExistingOne();
    } else if (this.commonsHttp.errorsHttp.isControlledError(error, 'E0071')) {
      this.snackbars.failureCheckIPNotAllowed();
    }
    this.commonsHttp.errorsHttp.communication(error);
  };

  // ==========================================================================================
  // Get Checks
  // ==========================================================================================

  public getChecks$(): Observable<Check[]> {
    return this.checksSubject.asObservable();
  }

  public getChecks(employeeID: number, params?: Params): void {
    if (!this.oAuthStorage.hasOAuth) {
      return;
    }

    this.http.getChecks$(employeeID, params).subscribe({
      next: this.nextGetChecks,
      error: this.errorGetChecks,
    });
  }

  private nextGetChecks = (data: CheckApiResponse): void => {
    if (this.commonsHttp.validationsHttp.verifyStatus200(data)) {
      const checks: Check[] = data.body.result;
      const paginationLinks: QarepaApiLinks = data.body.links;
      this.checksSubject.next(checks);
      this.paginationLinksSubject.next(paginationLinks);
    } else {
      this.checksSubject.next(null);
      this.paginationLinksSubject.next(null);
      this.commonsHttp.errorsHttp.apiInvalidResponse(data);
    }
  };

  private errorGetChecks = (error: HttpErrorResponse): void => {
    this.checksSubject.next(null);
    this.paginationLinksSubject.next(null);
    this.commonsHttp.errorsHttp.communication(error);
  };

  // ==========================================================================================
  // Update Check
  // ==========================================================================================

  public updateCheck(employeeID: number, checkID: number, check: Check): void {
    if (!this.oAuthStorage.hasOAuth) {
      return;
    }

    this.http.updateCheck$(employeeID, checkID, check).subscribe({
      next: this.nextUpdateCheck,
      error: this.errorUpdateCheck,
    });
  }

  private nextUpdateCheck = (data: CheckRegisterApiResponse): void => {
    if (
      this.commonsHttp.validationsHttp.verifyStatus200(data) ||
      this.commonsHttp.validationsHttp.verifyStatus201(data)
    ) {
      if (this.isCheck(data.body.result[0])) {
        const check: Check = data.body.result[0];
        const journey: Journey = data.body.included as unknown as Journey;
        this.checkSubject.next(check);
        this.journeySubject.next(journey);
        this.snackbars.successRegisterCheck();
      } else {
        const checkRequest: CheckEmployeeRequest = data.body.result[0];
        this.checkRequestSubject.next(checkRequest);
        this.snackbars.successRequestRegisterCheck();
      }
    } else {
      this.checkSubject.next(null);
      this.journeySubject.next(null);
      this.commonsHttp.errorsHttp.apiInvalidResponse(data);
    }
  };

  private errorUpdateCheck = (error: HttpErrorResponse): void => {
    this.checkSubject.next(null);
    this.journeySubject.next(null);
    if (this.commonsHttp.errorsHttp.isControlledError(error, 'E0004')) {
      this.snackbars.failureWrongHours();
    } else if (this.commonsHttp.errorsHttp.isControlledError(error, 'E0008')) {
      this.snackbars.failureEmployeeInvalidCredentials();
    } else if (this.commonsHttp.errorsHttp.isControlledError(error, 'E0020')) {
      this.snackbars.failureEmployeeNotWithInWorkplaceAccessRadius();
    } else if (this.commonsHttp.errorsHttp.isControlledError(error, 'E0028')) {
      this.snackbars.failureCheckDateCollidesWithAnExistingOne();
    } else if (this.commonsHttp.errorsHttp.isControlledError(error, 'E0030')) {
      this.snackbars.failureCheckCalendarIsClosed();
    } else if (this.commonsHttp.errorsHttp.isControlledError(error, 'E0069')) {
      this.snackbars.failureCheckDateCollidesWithAnExistingOne();
    } else if (this.commonsHttp.errorsHttp.isControlledError(error, 'E0071')) {
      this.snackbars.failureCheckIPNotAllowed();
    }
    this.commonsHttp.errorsHttp.communication(error);
  };

  // ==========================================================================================
  // Delete Check
  // ==========================================================================================

  public getMessage$(): Observable<string> {
    return this.messageSubject.asObservable();
  }

  public deleteCheck(employeeID: number, checkID: number): void {
    if (!this.oAuthStorage.hasOAuth) {
      return;
    }

    this.http.deleteCheck$(employeeID, checkID).subscribe({
      next: this.nextDeleteCheck,
      error: this.errorDeleteCheck,
    });
  }

  private nextDeleteCheck = (data: CheckRegisterApiResponse): void => {
    if (this.commonsHttp.validationsHttp.verifyStatus200(data)) {
      const journey: Journey = data.body.included as unknown as Journey;
      this.messageSubject.next('OK delete check');
      this.journeySubject.next(journey);
      this.snackbars.successDeleteCheckEmployee();
    } else if (this.commonsHttp.validationsHttp.verifyStatus201(data)) {
      if (!this.isCheck(data.body.result[0])) {
        this.checkRequestSubject.next(data.body.result[0]);
        this.snackbars.successRequestDeleteCheck();
      }
    } else {
      this.messageSubject.next(null);
      this.journeySubject.next(null);
      this.commonsHttp.errorsHttp.apiInvalidResponse(data);
    }
  };

  private errorDeleteCheck = (error: HttpErrorResponse): void => {
    this.messageSubject.next(null);
    this.journeySubject.next(null);
    this.commonsHttp.errorsHttp.communication(error);
  };

  // ==========================================================================================
  // Send Checks Report
  // ==========================================================================================

  public getChecksReport$(): Observable<CheckReport> {
    return this.checksReportSubject.asObservable().pipe(shareReplay(1));
  }

  public sendChecksReport(employeeID: number, month: Month): void {
    if (!this.oAuthStorage.hasOAuth) {
      return;
    }

    this.http.sendChecksReport$(employeeID, month).subscribe({
      next: this.nextSendChecksReport,
      error: this.errorSendChecksReport,
    });
  }

  private nextSendChecksReport = (data: CheckReportApiResponse): void => {
    if (this.commonsHttp.validationsHttp.verifyStatus200(data)) {
      this.checksReportSubject.next(data.body.result[0]);
      this.snackbars.successSentReportByEmail();
    } else {
      this.checksReportSubject.next(null);
      this.commonsHttp.errorsHttp.apiInvalidResponse(data);
    }
  };

  private errorSendChecksReport = (error: HttpErrorResponse): void => {
    this.checksReportSubject.next(null);
    if (this.commonsHttp.errorsHttp.isControlledError(error, 'E0004')) {
      this.snackbars.failureInvalidField();
    }
    this.commonsHttp.errorsHttp.communication(error);
  };

  public checkHistory(companyID: number, employeeID: number, checkID: number) {
    this.http.checkHistory$(companyID, employeeID, checkID).subscribe({
      next: this.nextCheckHistory,
      error: this.errorCheckHistory,
    });
  }

  private nextCheckHistory = (response: CheckHistoryApiObject) => {
    if (!response) {
      this.checkHistorySubject.next([]);
      return;
    }

    this.checkHistorySubject.next(response.result);
  };

  private errorCheckHistory = () => {
    this.checkHistorySubject.next([]);
  };

  // ==========================================================================================
  // Pagination Links
  // ==========================================================================================

  public getPaginationLinks$(): Observable<QarepaApiLinks> {
    return this.paginationLinksSubject.asObservable();
  }

  private isCheck(data: Check | CheckEmployeeRequest): data is Check {
    return (
      typeof data === 'object' &&
      data !== null &&
      'calendarId' in data &&
      typeof (data as Check).calendarId === 'number'
    );
  }
}
